import type { ChargerNetworkPatch, ChargerNetworkRow, ChargerNetworkView } from './api';

/**
 * The Charger network card's rules (Warren, 4 Nov 2026, The Vered; ruling S3). Pure, so the form's checks have tests and match
 * what the Api refuses (Api lib/simFallback/networkLink.ts): a save never offers a write the server will turn down.
 */

export const NETWORK_ID_MAX = 64;

export interface NetworkDraft {
  link: '' | 'wifi' | 'sim';
  /** 'paused' or 'active'. */
  sim: 'paused' | 'active';
  fallback: boolean;
  breaker_id: string;
  panel_id: string;
}

export const LINK_OPTIONS: ReadonlyArray<{ value: NetworkDraft['link']; label: string }> = [
  { value: '', label: 'Not set' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'sim', label: 'SIM' },
];

export const SIM_OPTIONS: ReadonlyArray<{ value: NetworkDraft['sim']; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export function draftOf(row: ChargerNetworkRow): NetworkDraft {
  return {
    link: row.link ?? '',
    sim: row.sim_paused ? 'paused' : 'active',
    fallback: row.fallback_enabled,
    breaker_id: row.breaker_id ?? '',
    panel_id: row.panel_id ?? '',
  };
}

/** Only what changed, so a save never restates, and never overwrites, what the watcher moved since the card was read. */
export function patchOf(row: ChargerNetworkRow, draft: NetworkDraft): ChargerNetworkPatch {
  const patch: ChargerNetworkPatch = {};
  if (draft.link !== (row.link ?? '')) patch.link = draft.link === '' ? null : draft.link;
  if ((draft.sim === 'paused') !== row.sim_paused) patch.sim_paused = draft.sim === 'paused';
  if (draft.fallback !== row.fallback_enabled) patch.fallback_enabled = draft.fallback;
  for (const field of ['breaker_id', 'panel_id'] as const) {
    const next = draft[field].trim();
    if (next !== (row[field] ?? '')) patch[field] = next === '' ? null : next;
  }
  return patch;
}

export const isDirty = (row: ChargerNetworkRow, draft: NetworkDraft): boolean => Object.keys(patchOf(row, draft)).length > 0;

/** The plain reason a draft cannot be saved, or null. */
export function draftProblem(row: ChargerNetworkRow, draft: NetworkDraft): string | null {
  const patch = patchOf(row, draft);
  if ('breaker_id' in patch && (patch.breaker_id?.length ?? 0) > NETWORK_ID_MAX) return `The breaker ID can be at most ${NETWORK_ID_MAX} characters.`;
  if ('panel_id' in patch && (patch.panel_id?.length ?? 0) > NETWORK_ID_MAX) return `The panel ID can be at most ${NETWORK_ID_MAX} characters.`;
  if (('link' in patch || 'sim_paused' in patch) && draft.sim === 'paused' && draft.link !== 'wifi') return 'A paused SIM cannot be the charger\'s link. Set the link to Wi-Fi, or the SIM to Active.';
  if ('sim_paused' in patch && draft.sim === 'active' && draft.link === 'wifi') return 'An active SIM carries the charger. Set the link to SIM as well.';
  return null;
}

/**
 * The drafts after a reload that moved the rows under them: each keeps only the fields the person changed against the row the
 * card had, and takes the rest from the row as it is now. Kept whole, an old link or SIM would read as a change and overwrite
 * what the watcher just did.
 */
export function rebaseDrafts(oldRows: readonly ChargerNetworkRow[], drafts: Readonly<Record<string, NetworkDraft>>, newRows: readonly ChargerNetworkRow[]): Record<string, NetworkDraft> {
  const out: Record<string, NetworkDraft> = {};
  for (const [id, draft] of Object.entries(drafts)) {
    const was = oldRows.find((r) => r.charger_id === id);
    const now = newRows.find((r) => r.charger_id === id);
    if (!now) continue;
    const base = was ? draftOf(was) : draft;
    out[id] = {
      link: draft.link !== base.link ? draft.link : draftOf(now).link,
      sim: draft.sim !== base.sim ? draft.sim : draftOf(now).sim,
      fallback: draft.fallback !== base.fallback ? draft.fallback : draftOf(now).fallback,
      breaker_id: draft.breaker_id !== base.breaker_id ? draft.breaker_id : draftOf(now).breaker_id,
      panel_id: draft.panel_id !== base.panel_id ? draft.panel_id : draftOf(now).panel_id,
    };
  }
  return out;
}

/** One line under the card's hint: what the Watcher switches do just now. Null: nothing to add. */
export function watcherLine(mode: ChargerNetworkView['watcher_mode']): string | null {
  if (mode === 'off') return 'The 1NCE watcher is off across the network, so the Watcher switches below do nothing until it is turned on.';
  if (mode === 'shadow') return 'The 1NCE watcher is in shadow: it calls nothing, writes down what it would do, and asks EVI ops to turn a silent charger\'s SIM back on by hand.';
  if (mode === 'by_hand') return 'No 1NCE credentials yet: the watcher asks EVI ops to turn a silent charger\'s SIM back on by hand.';
  return null;
}

/** A row whose record cannot do what it appears to, or null. Times read in the site's own zone. */
export function rowWarning(row: ChargerNetworkRow, mode: ChargerNetworkView['watcher_mode'], zone?: string | null): string | null {
  if (row.sim_paused && !row.fallback_enabled) return 'SIM paused with the Watcher off: nothing turns it back on if Wi-Fi fails.';
  if (row.sim_paused && mode === 'off') return 'SIM paused, and the watcher is off across the network: nothing turns it back on if Wi-Fi fails.';
  if (row.sim_paused && row.review_status === 'commissioning') return 'SIM paused, and the watcher does not act on a charger in commissioning.';
  if (row.sim_paused && row.maintenance_hold_until) return `SIM paused, and the watcher is holding for a maintenance window until ${siteTime(row.maintenance_hold_until, zone)}.`;
  if (row.link === 'wifi' && !row.sim_paused && row.has_sim) return 'Recorded on Wi-Fi with an active SIM. If that SIM is really on, this charger is on the carrier, and its frames do not prove the site\'s Wi-Fi.';
  return null;
}

/** 'Nov 4, 7:14 PM' in the site's own zone. */
export function siteTime(at: string | null | undefined, zone: string | null | undefined): string {
  if (!at) return '';
  const when = new Date(at);
  if (Number.isNaN(when.getTime())) return '';
  const format = (timeZone: string | undefined) => new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(when).replace(/\s+/g, ' ');
  try {
    return format(zone ?? undefined);
  } catch {
    return format(undefined);
  }
}
